// Page structure every screen shares, so the console reads as one instrument:
// a header with title, one-line purpose and actions; a summary strip of the
// numbers that have limits; an empty state that says what fills it. Styled
// only through the theme; no colors or sizes here.

import { ActionIcon, CopyButton, Box, DataList, EmptyState as MantineEmptyState, Group, Paper, SimpleGrid, Skeleton, Stack, Text, Title, type MantineColor } from '@mantine/core';
import { IconCheck, IconCopy, type Icon } from '@tabler/icons-react';
import type { ReactNode } from 'react';

import { ICON_INLINE } from '../theme';


// Every page opens the same way: the title at display size, the one-line
// purpose under it, and room before anything else. When the page has
// counts, they follow as one band of text on the surface, bordered and
// rounded like the cards below it, never tiles. The primary action is not
// here; it sits on the toolbar row above the table.
export function PageHeader({ title, description, stats, statsCount = 4 }: {
  title: string;
  description?: ReactNode;
  /** The page's counts: `'loading'` keeps their place while they load; omitted means the page has none. */
  stats?: StatProps[] | 'loading' | undefined;
  statsCount?: number;
}) {
  return (
    <Box component="header" mb="xl">
      <Stack gap={6} miw={0}>
        <Title order={1}>{title}</Title>
        {/* The purpose runs the width of the page, as the tables and cards under it do. */}
        {description && <Text size="sm" c="dimmed">{description}</Text>}
      </Stack>
      {stats !== undefined && (
        <Box mt="xl" className="stats-band">
          {stats === 'loading' ? <StatStripSkeleton count={statsCount} bare /> : <StatStrip stats={stats} bare />}
        </Box>
      )}
    </Box>
  );
}

/**
 * The page's action, at the far right of the toolbar row above the table it
 * acts on, at input height: a small button with its icon, never a large one
 * in the header. A note beside it says why it cannot be used and who can
 * change that.
 */
export function ToolbarActions({ note, noteId, children }: { note?: ReactNode; noteId?: string | undefined; children: ReactNode }) {
  return (
    <Group gap="sm" align="center" wrap="wrap" justify="flex-end" ml="auto">
      {note && <Text id={noteId} size="xs" c="dimmed" maw={320} ta="right">{note}</Text>}
      {children}
    </Group>
  );
}

export interface StatProps {
  label: string;
  /** The number, ideally as a fraction of its limit ("2 of 20"). */
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'ok' | 'warn' | 'bad' | 'neutral';
}

const toneColor: Record<NonNullable<StatProps['tone']>, MantineColor> = { ok: 'success', warn: 'warning', bad: 'danger', neutral: 'var(--mantine-color-text)' };

const stripCols = (count: number) => ({ base: 2, sm: Math.min(count, 4) });

/** A row of the numbers an administrator came to check. Text, not hero tiles; ruled like a notebook table inside the header band. */
export function StatStrip({ stats, bare = false }: { stats: StatProps[]; bare?: boolean }) {
  const grid = (
    <SimpleGrid cols={stripCols(stats.length)} spacing="lg" className={bare ? 'stats-ruled' : undefined}>
      {stats.map((s) => (
        <Box key={s.label} className="stat">
          <Text size="xs" c="dimmed" fw={500}>{s.label}</Text>
          <Text size="xl" fw={600} className="num" c={toneColor[s.tone ?? 'neutral']} lh={1.2} mt={2}>{s.value}</Text>
          {s.hint && <Text size="xs" c="dimmed" mt={2}>{s.hint}</Text>}
        </Box>
      ))}
    </SimpleGrid>
  );
  // `bare` is the grid alone, for a header band that already frames it.
  return bare ? grid : <Paper p="md" mb="lg">{grid}</Paper>;
}

/**
 * The strip while its numbers load: the same bordered row and the same three
 * lines per figure (label, value, hint) at their line heights, so nothing
 * moves when the numbers land.
 */
export function StatStripSkeleton({ count, bare = false }: { count: number; bare?: boolean }) {
  const grid = (
    <SimpleGrid cols={stripCols(count)} spacing="lg" aria-hidden="true" className={bare ? 'stats-ruled' : undefined}>
      {Array.from({ length: count }, (_, i) => (
        <Box key={i} className="stat">
          <Skeleton height={12} width="45%" mt={2} />
          <Skeleton height={22} width="35%" mt={6} />
          <Skeleton height={12} width="70%" mt={6} mb={2} />
        </Box>
      ))}
    </SimpleGrid>
  );
  return bare ? grid : <Paper p="md" mb="lg" aria-hidden="true">{grid}</Paper>;
}

/**
 * What fills an empty table or list, as a heading under the page's, and the
 * action that starts it. `compact` is for a card that already has its own
 * heading above the empty state.
 */
export function EmptyState({ icon: IconComponent, title, body, action, compact = false }: { icon: Icon; title: string; body?: ReactNode; action?: ReactNode; compact?: boolean }) {
  return (
    <MantineEmptyState icon={<IconComponent stroke={1.75} />} {...(compact ? { pt: 'xs', pb: 'xl' } : { py: 48 })} px="lg">
      <MantineEmptyState.Title order={3}>{title}</MantineEmptyState.Title>
      {body && <MantineEmptyState.Description>{body}</MantineEmptyState.Description>}
      {action && <MantineEmptyState.Actions>{action}</MantineEmptyState.Actions>}
    </MantineEmptyState>
  );
}

/** A labelled block inside a pane: a small sentence-case label, then rows. Sections are ruled off from each other (`.pane-sections`). */
export function DrawerSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack gap="sm" component="section" className="pane-section">
      <Title order={3} fz="sm" fw={600} lh={1.3}>{title}</Title>
      {children}
    </Stack>
  );
}

// The value sits against the right edge and wraps rather than truncating, so
// an identifier is always there in full (The Identifier Rule).
const FACTS_STYLES = {
  item: { justifyContent: 'space-between', gap: 'var(--mantine-spacing-md)' },
  itemValue: { minWidth: 0, textAlign: 'right', whiteSpace: 'normal', overflowWrap: 'anywhere' },
} as const;

// An identifier too long for half a row takes a row of its own under its
// label: whole, left-aligned on the recessed surface, with a copy control,
// so it is read and carried rather than wrapped ragged against the edge.
const LONG_ID = 22;

/** An identifier whole, left-aligned on the recessed surface, with a copy control (The Identifier Rule). */
export function IdBox({ value, label, mt = 0 }: { value: string; label: string; mt?: number }) {
  return (
    <Group gap="xs" wrap="nowrap" align="center" mt={mt} pl="sm" pr={4} py={4} style={{ background: 'var(--mantine-color-default-hover)', borderRadius: 'var(--mantine-radius-sm)' }}>
      <Text span className="id" size="sm" flex={1} miw={0} style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{value}</Text>
      <CopyButton value={value} timeout={2000}>
        {({ copied, copy }) => (
          <ActionIcon variant="subtle" color="gray" size={32} onClick={copy} aria-label={copied ? `${label} copied` : `Copy ${label.toLowerCase()}`} style={{ flex: 'none' }}>
            {copied ? <IconCheck {...ICON_INLINE} /> : <IconCopy {...ICON_INLINE} />}
          </ActionIcon>
        )}
      </CopyButton>
    </Group>
  );
}

/** Label / value rows for details, as a description list. */
export function Facts({ rows }: { rows: { label: string; value: ReactNode; mono?: boolean }[] }) {
  return (
    <DataList gap={10} labelWidth="40%" styles={FACTS_STYLES}>
      {rows.map((r) => {
        const long = r.mono && typeof r.value === 'string' && r.value.length > LONG_ID ? r.value : null;
        if (long) {
          return (
            <DataList.Item key={r.label} style={{ display: 'block' }}>
              <DataList.ItemLabel>{r.label}</DataList.ItemLabel>
              <DataList.ItemValue style={{ textAlign: 'left', display: 'block' }}>
                <IdBox value={long} label={r.label} mt={4} />
              </DataList.ItemValue>
            </DataList.Item>
          );
        }
        return (
          <DataList.Item key={r.label}>
            <DataList.ItemLabel>{r.label}</DataList.ItemLabel>
            <DataList.ItemValue className={r.mono ? 'id' : 'num'}>{r.value}</DataList.ItemValue>
          </DataList.Item>
        );
      })}
    </DataList>
  );
}
